import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import type { UseFormHandleSubmit, UseFormRegister } from 'react-hook-form';
import type { MultiMethodLoginFormData } from './types';

interface UiConfig {
  headerTitle: string;
  headerSubtitle: string;
  headerSpacing: string;
  inputHeight: string;
  primaryBtnHeight: string;
  sectionGap: string;
}

interface PasswordStepProps {
  ui: UiConfig;
  email: string;
  error: string | null;
  showPassword: boolean;
  onToggleShowPassword: () => void;
  isPasswordValid: boolean;
  isSubmitting: boolean;
  register: UseFormRegister<MultiMethodLoginFormData>;
  handleSubmit: UseFormHandleSubmit<MultiMethodLoginFormData>;
  onSubmitPassword: (data: MultiMethodLoginFormData) => void | Promise<void>;
  onBack: () => void;
}

export const PasswordStep: React.FC<PasswordStepProps> = ({
  ui,
  email,
  error,
  showPassword,
  onToggleShowPassword,
  isPasswordValid,
  isSubmitting,
  register,
  handleSubmit,
  onSubmitPassword,
  onBack
}) => {
  const { t } = useTranslation('auth');
  return (
    <>
      <div className="sr-only">Password for {email}</div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-5"
          >
            <div className="rounded-xl border border-red-100 bg-red-50 p-4">
              <p className="text-center text-sm text-red-600">{error}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit(onSubmitPassword)} className={ui.sectionGap}>
        <div className="space-y-2">
          <label
            htmlFor="password"
            className="block text-sm font-semibold text-gray-800"
          >
            {t('multiMethod.passwordLabel')}
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-inner">
                <Lock className="h-4 w-4 text-emerald-500" />
              </div>
            </div>
            <input
              {...register('password')}
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder={t('multiMethod.passwordPlaceholder')}
              autoComplete="current-password"
              autoFocus
              className={
                `w-full ${ui.inputHeight} rounded-2xl border border-gray-200 pl-14 pr-12 text-base ` +
                'hover:border-gray-300 focus:border-emerald-500 focus:outline-hidden focus:ring-4 ' +
                'bg-slate-50 text-gray-900 transition-all duration-200 placeholder:text-gray-400 focus:ring-emerald-500/15'
              }
            />
            <button
              type="button"
              onClick={onToggleShowPassword}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <label className="group flex cursor-pointer items-center">
            <input
              type="checkbox"
              {...register('rememberMe')}
              className="h-4 w-4 rounded-sm border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="ml-2 text-gray-600 group-hover:text-gray-800">
              {t('multiMethod.rememberMe')}
            </span>
          </label>
          <a
            href="/forgot-password"
            className="font-medium text-emerald-600 hover:text-emerald-700"
          >
            {t('multiMethod.forgotPassword')}
          </a>
        </div>

        <motion.button
          type="submit"
          disabled={!isPasswordValid || isSubmitting}
          whileHover={isPasswordValid && !isSubmitting ? { scale: 1.01 } : {}}
          whileTap={isPasswordValid && !isSubmitting ? { scale: 0.99 } : {}}
          className={
            `w-full ${ui.primaryBtnHeight} rounded-2xl text-base font-semibold transition-all duration-200 ` +
            'flex items-center justify-center gap-2 ' +
            (isPasswordValid && !isSubmitting
              ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/25 hover:bg-emerald-500'
              : 'cursor-not-allowed bg-gray-100 text-gray-400')
          }
        >
          {isSubmitting ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            t('multiMethod.submitPassword')
          )}
        </motion.button>
      </form>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-500 transition-colors hover:text-gray-700"
        >
          {t('multiMethod.backToOptions')}
        </button>
      </div>
    </>
  );
};
